import { motion } from "framer-motion";
import { Check, BookOpen, ShoppingBag, Sparkles } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { IMAGES } from "./content";

const Bullet = ({ children }) => (
  <li className="flex items-center gap-3 text-sm font-medium md:text-base">
    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-dm-keys">
      <Check className="h-3 w-3 text-dm-ink" strokeWidth={3} />
    </span>
    {children}
  </li>
);

export const BusinessModel = () => (
  <section id="pilares" className="relative bg-dm-sand py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8">
      <div className="mb-14 max-w-3xl">
        <Reveal>
          <Overline color="#FF7A00">Modelo de negócio</Overline>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-5xl font-black leading-[0.9] tracking-tighter text-dm-ink md:text-6xl lg:text-7xl">
            Três frentes de escala,
            <br />
            <span className="text-dm-muted">uma só história.</span>
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
        {/* Publishing — large */}
        <Reveal className="md:col-span-7" delay={0.05}>
          <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] bg-white">
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.bookSpread}
                alt="Livro ilustrado da turma DóRéMigos"
                className="h-64 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105 md:h-80"
              />
              <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-dm-guita px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                <BookOpen className="h-3.5 w-3.5" /> Publishing
              </span>
            </div>
            <div className="flex flex-1 flex-col p-7 md:p-9">
              <h3 className="font-display text-3xl font-bold tracking-tight text-dm-ink">
                A história vem primeiro.
              </h3>
              <p className="mt-3 max-w-lg text-base leading-relaxed text-dm-muted">
                Livros ilustrados que apresentam a turma, constroem o cânone do universo e criam o
                primeiro vínculo emocional — a base de tudo que vem depois.
              </p>
              <ul className="mt-6 flex flex-col gap-3 text-dm-ink">
                <Bullet>Coleção de histórias ilustradas</Bullet>
                <Bullet>Livros sonoros e de atividades</Bullet>
                <Bullet>Co-edição com editoras parceiras</Bullet>
              </ul>
            </div>
          </article>
        </Reveal>

        {/* Merchandising — tall */}
        <Reveal className="md:col-span-5" delay={0.12}>
          <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] bg-dm-ink text-dm-cream">
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.backpacksWall}
                alt="Mochilas-personagem e pelúcias premium"
                className="h-72 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105 md:h-80"
              />
              <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-dm-tumtum px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                <ShoppingBag className="h-3.5 w-3.5" /> Merchandising
              </span>
            </div>
            <div className="flex flex-1 flex-col p-7 md:p-9">
              <h3 className="font-display text-3xl font-bold tracking-tight">
                O objeto é o próprio personagem.
              </h3>
              <p className="mt-3 text-base leading-relaxed text-dm-cream/70">
                Pelúcias premium, mochilas temáticas, vestuário e papelaria. Cada item leva a
                narrativa para o dia a dia e vira companhia — não só produto.
              </p>
              <ul className="mt-6 flex flex-col gap-3">
                <li className="flex items-center gap-3 text-sm font-medium md:text-base">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-dm-keys">
                    <Check className="h-3 w-3 text-dm-ink" strokeWidth={3} />
                  </span>
                  Pelúcias premium colecionáveis
                </li>
                <li className="flex items-center gap-3 text-sm font-medium md:text-base">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-dm-keys">
                    <Check className="h-3 w-3 text-dm-ink" strokeWidth={3} />
                  </span>
                  Mochilas-personagem e vestuário
                </li>
                <li className="flex items-center gap-3 text-sm font-medium md:text-base">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-dm-keys">
                    <Check className="h-3 w-3 text-dm-ink" strokeWidth={3} />
                  </span>
                  Papelaria e kits de festa
                </li>
              </ul>
            </div>
          </article>
        </Reveal>

        {/* Live experiences — wide */}
        <Reveal className="md:col-span-12" delay={0.05}>
          <article className="group grid grid-cols-1 overflow-hidden rounded-[2rem] bg-white lg:grid-cols-12">
            <div className="relative overflow-hidden lg:col-span-6">
              <img
                src={IMAGES.playArea}
                alt="Área kids imersiva em shopping"
                className="h-64 w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105 lg:h-full"
              />
              <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-dm-vi px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                <Sparkles className="h-3.5 w-3.5" /> Live · Experiências
              </span>
            </div>
            <div className="flex flex-col justify-center p-7 lg:col-span-6 md:p-12">
              <h3 className="font-display text-3xl font-bold tracking-tight text-dm-ink md:text-4xl">
                O espaço vira palco.
              </h3>
              <p className="mt-3 max-w-lg text-base leading-relaxed text-dm-muted md:text-lg">
                Áreas kids e eventos imersivos em shoppings: piano gigante, estúdio de voz e o
                brinquedo TumTum. Fluxo, tempo de permanência e marca viva.
              </p>
              <ul className="mt-6 grid gap-3 text-dm-ink sm:grid-cols-2">
                <Bullet>Áreas kids licenciadas</Bullet>
                <Bullet>Turnês e ativações sazonais</Bullet>
                <Bullet>Meet &amp; greet com a turma</Bullet>
                <Bullet>Cenografia modular</Bullet>
              </ul>
            </div>
          </article>
        </Reveal>
      </div>
    </div>
  </section>
);
